import React, { useRef, useState } from 'react';
import { InsuranceOffer, ProductVariant } from 'types/Product';
import styled from 'styled-components';
import { InsuranceContent, InsuranceValue } from 'types/ProductResponseType';
import Button from 'components/Button/Button';
import { Tabs, TabPanel, TypographyComponent, Link, Alert, Modal } from '@tvg/fuse-design';
import { enableMonthlyProtectInsuranceTempOffer } from 'constants/promotion-voucher-constants';
import { getSite } from 'context/SiteContext';
import { InsuraceDescription } from '../InsuranceDescription/InsuranceDescription';
import { DialogProps } from '../Shared/SharedProps';

enum TextTypes {
  bold = 'bold',
  regular = 'regular',
  link = 'link',
}

enum InsuranceTypes {
  monthly = 'Monthly Insurance',
  yearly = 'Fixed term insurance',
}
interface Props extends DialogProps {
  options?: InsuranceOffer[];
  open: boolean;
  selectedSkuInsuranceContent: InsuranceContent;
  handleSelectedInsurance: (option: InsuranceOffer | null) => void;
  selectedInsurance?: InsuranceOffer | string | null;
  findOutMoreView: boolean;
  handleContinueWithoutInsurance: () => void;
  onAddInsuranceClick: () => void;
  selectedSku: ProductVariant;
}

const BottomDescriptionContainer = styled.div`
  padding-bottom: 20px 0 10px 0;
`;

const StyledLink = styled(Link)`
  display: inline;
  color: var(--color-global-black) !important;
`;

const StyledTypography = styled(TypographyComponent)`
  margin: var(--spacing-space16) 0;
`;

const StyledDialogNotification = styled.div`
  padding-top: var(--spacing-space16);
`;

const StyledTabPanel = styled(Tabs)`
  @media (min-width: 48rem) {
    &:first-child {
      :first-child {
        :first-child {
          margin-left: 0;
        }
      }
    }
  }
  div: {
    div: {
      overflow-x: none;
    }
  }

  &:first-child {
    :first-child {
      :first-child {
        margin-left: 0;
      }
    }
  }

  button {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    p {
      width: auto;
    }
  }
`;

export const AddToBasketDialogContainer = styled.div`
  margin-top: var(--spacing-space24);
  width: 100%;
  @media (min-width: 30rem) {
    margin: 0 auto;
    width: max-content;
  }
`;

const SelectInsuranceButtonContainer = styled.div`
  margin-bottom: var(--spacing-space16);
`;

export function CheckItsRightDialog({
  selectedSku,
  options,
  onClose,
  open,
  selectedSkuInsuranceContent,
  handleSelectedInsurance,
  selectedInsurance,
  findOutMoreView,
  handleContinueWithoutInsurance,
  onAddInsuranceClick,
}: Props) {
  const [insuranceType, setInsuranceType] = useState(InsuranceTypes.monthly);
  const scrollAnchor = useRef<HTMLDivElement>(null);

  const isScroll =
    scrollAnchor.current &&
    scrollAnchor.current.parentElement &&
    scrollAnchor.current.parentElement.scrollHeight > scrollAnchor.current.parentElement.clientHeight;
  const { checkIfRightMonthly, checkIfRightYearly, covered, notCovered, value, valueAfter } =
    selectedSkuInsuranceContent.insuranceContent;
  const showTabs = checkIfRightMonthly && checkIfRightYearly && findOutMoreView;
  const isMonthly =
    selectedInsurance &&
    typeof selectedInsurance !== 'string' &&
    !findOutMoreView &&
    selectedInsurance.offer.productDescription.split(' ').includes('Monthly');

  const renderContent = (content: InsuranceValue[][]) =>
    content.map((paragraph) => (
      <TypographyComponent size="m1" tag="p" type="paragraph">
        {paragraph.map((line) => {
          if (line.type === TextTypes.bold) {
            if (line.text?.includes('By adding insurance')) {
              return (
                <StyledDialogNotification>
                  <TypographyComponent size="m1" color="var(--color-global-black)" tag="span" type="heading">
                    {line.text}
                    <br />
                  </TypographyComponent>
                </StyledDialogNotification>
              );
            }
            return (
              <TypographyComponent size="m1" color="var(--color-global-black)" tag="span" type="heading">
                {line.text}
                <br />
              </TypographyComponent>
            );
          }
          if (line.type === TextTypes.regular) {
            return (
              <TypographyComponent size="m1" tag="span" type="paragraph">
                {line.text}
              </TypographyComponent>
            );
          }

          if (line.type === TextTypes.link) {
            return (
              <StyledLink target="_blank" href={line.url}>
                <TypographyComponent size="m1" tag="span" type="paragraph" color="var(--color-global-black)">
                  {` ${line.text} `}
                </TypographyComponent>
              </StyledLink>
            );
          }

          return null;
        })}
      </TypographyComponent>
    ));

  const getTitle = () => {
    if (!findOutMoreView && selectedInsurance && typeof selectedInsurance !== 'string') {
      const shouldAddMonthlyTitle = selectedInsurance.offer.termDescription.includes('Monthly');
      return `${selectedInsurance?.offer?.termDescription} £${selectedInsurance?.offer?.sellingPrice}${shouldAddMonthlyTitle ? ' a month' : ''}`;
    }
    return (
      selectedSkuInsuranceContent?.insuranceContent.pdpInsuranceDescription[0][0].text ||
      selectedSkuInsuranceContent?.insuranceContent.pdpInsuranceDescription[0][0].title
    )?.replace('Insurance', '');
  };

  const resetStateAfterAnimationCompleted = () => {
    setTimeout(() => setInsuranceType(InsuranceTypes.monthly), 200);
  };

  const handleModalClose = () => {
    onClose();
    if (insuranceType !== InsuranceTypes.monthly) {
      resetStateAfterAnimationCompleted();
    }
  };

  const handleAddInsuranceClick = (insuranceOffer: InsuranceOffer) => {
    handleSelectedInsurance(insuranceOffer);
    onAddInsuranceClick();
    resetStateAfterAnimationCompleted();
  };

  const handleNoInsurance = () => {
    handleContinueWithoutInsurance();
    resetStateAfterAnimationCompleted();
  };

  const getInsuraceButtons = () =>
    selectedSku?.insuranceOffers?.map((insuranceOffer) => {
      if (
        showTabs &&
        insuranceOffer?.offer.termDescription === insuranceType &&
        insuranceType === InsuranceTypes.monthly
      ) {
        return (
          <SelectInsuranceButtonContainer>
            <Button
              id="insurance-add-button"
              onClick={() => handleAddInsuranceClick(insuranceOffer)}
              dataTestId="check-its-right-add-button"
            >
              {`${insuranceOffer?.offer.termDescription} £${insuranceOffer?.offer.sellingPrice} a month`}
            </Button>
          </SelectInsuranceButtonContainer>
        );
      }
      if (
        showTabs &&
        insuranceOffer?.offer.termDescription !== InsuranceTypes.monthly &&
        insuranceType === InsuranceTypes.yearly
      ) {
        return (
          <SelectInsuranceButtonContainer>
            <Button
              id="insurance-add-button"
              onClick={() => handleAddInsuranceClick(insuranceOffer)}
              dataTestId="check-its-right-add-button"
            >
              {`${insuranceOffer?.offer.termDescription} £${insuranceOffer?.offer.sellingPrice}`}
            </Button>
          </SelectInsuranceButtonContainer>
        );
      }

      if (!showTabs) {
        return (
          <SelectInsuranceButtonContainer>
            <Button
              id="insurance-add-button"
              onClick={() => handleAddInsuranceClick(insuranceOffer)}
              dataTestId="check-its-right-add-button"
            >
              {`${insuranceOffer?.offer.termDescription} £${insuranceOffer?.offer.sellingPrice}`}
            </Button>
          </SelectInsuranceButtonContainer>
        );
      }

      return null;
    });

  const handleTypeOfInsuranceClick = ({ target }: { target: HTMLElement }) => {
    if (target?.firstElementChild?.innerHTML === InsuranceTypes.monthly) {
      setInsuranceType(InsuranceTypes.monthly);
    }

    if (target?.firstElementChild?.innerHTML === InsuranceTypes.yearly) {
      setInsuranceType(InsuranceTypes.yearly);
    }
  };

  const getMonthlyInsuranceTemporaryText = () => {
    const StyledMonthlyTempText = styled(TypographyComponent)`
      margin-bottom: var(--spacing-paragraph-margin-bottom-m1);
    `;

    return (
      <StyledMonthlyTempText size="m1" color="var(--color-global-black)" tag="h3" type="heading">
        *Get 12 months FREE insurance. Offer Ends 12/06/25. Buy Protect - Repair Monthly insurance with this item on
        your Very Pay credit account and the first 12 months are on us. The monthly premium and a refund of it will show
        on your statement each month for the first 12 months. You can cancel any time in your online account if you
        don’t want the policy to continue. To qualify each month, your Very Pay account must remain open and not be in
        arrears. Representative 44.9% APR Variable.
      </StyledMonthlyTempText>
    );
  };

  return options ? (
    <Modal
      title={getTitle()}
      dataTestId="check-its-right-dialog"
      srLabelBackButton="check_its_right_back_button"
      srLabelCloseButton="check its right_dialog"
      open={open}
      zIndex="20"
      onClose={handleModalClose}
      key={`check-its-right-dialog-${findOutMoreView}`}
    >
      <div data-testId="check-if-its-right-scroll-anchor" ref={scrollAnchor} />
      {isScroll && (
        <Alert
          variant="information"
          message="Scroll down to read and confirm if this insurance is right for you."
          dataTestId="insurance-check-its-right-info"
        />
      )}
      {isMonthly &&
        enableMonthlyProtectInsuranceTempOffer &&
        getSite() === 'very' &&
        getMonthlyInsuranceTemporaryText()}

      {value && renderContent(value)}
      {covered && notCovered && (
        <InsuraceDescription
          coverdTitle={covered.text}
          notCoveredTitle={notCovered.text}
          covered={covered.bulletText}
          notCovered={notCovered.bulletText}
          withDescription={false}
        />
      )}
      {showTabs && (
        <BottomDescriptionContainer>
          <StyledTabPanel
            className="insurance-fuse-tabs"
            landOnTab={1}
            tabListAriaLabel="Insurance Tabs"
            onClick={handleTypeOfInsuranceClick}
          >
            <TabPanel label={InsuranceTypes.monthly}>{renderContent(checkIfRightMonthly)}</TabPanel>

            <TabPanel label={InsuranceTypes.yearly}>{renderContent(checkIfRightYearly)}</TabPanel>
          </StyledTabPanel>
        </BottomDescriptionContainer>
      )}
      {!showTabs && !isMonthly && checkIfRightYearly && renderContent(checkIfRightYearly)}
      {!showTabs && isMonthly && checkIfRightMonthly && renderContent(checkIfRightMonthly)}
      {valueAfter && renderContent(valueAfter)}

      <StyledTypography color="var(--color-global-black)" size="m1" tag="p">
        Choose an option below
      </StyledTypography>
      <AddToBasketDialogContainer>
        {!findOutMoreView && selectedInsurance && typeof selectedInsurance !== 'string' ? (
          <SelectInsuranceButtonContainer>
            <Button id="insurance-add-button" onClick={onAddInsuranceClick} dataTestId="check-its-right-add-button">
              {`${selectedInsurance?.offer.termDescription} £${selectedInsurance?.offer.sellingPrice} ${selectedInsurance?.offer.termDescription === InsuranceTypes.monthly ? 'a month' : ''}`}
            </Button>
          </SelectInsuranceButtonContainer>
        ) : (
          <>{getInsuraceButtons()}</>
        )}
        <Button variant="tertiary" onClick={handleNoInsurance}>
          Continue without insurance
        </Button>
      </AddToBasketDialogContainer>
    </Modal>
  ) : null;
}

export default CheckItsRightDialog;
